// utils/ReferenceRangeCalculator.js
import { AgeCalculator } from './AgeCalculator';

export class ReferenceRangeCalculator {
  // Find the best matching reference range
  static findBestReferenceRange(parameter, patientAge, patientGender, patientAgeInDays = null) {
    if (!parameter.referenceRanges || parameter.referenceRanges.length === 0) {
      return {
        range_from: null,
        range_to: null,
        cric_range_from: null,
        cric_range_to: null,
        displayText: parameter.referenceRangeText || 'N/A',
        matched: false,
        matchType: 'text'
      };
    }

    // Use provided age in days or calculate from age string
    const ageInDays = patientAgeInDays || AgeCalculator.parseAgeToDays(patientAge);
    
    if (!patientGender) {
      return {
        range_from: null,
        range_to: null,
        cric_range_from: null,
        cric_range_to: null,
        displayText: parameter.referenceRangeText || 'N/A',
        matched: false,
        matchType: 'no_gender'
      };
    }

    // Filter ranges by gender first
    const genderRanges = parameter.referenceRanges.filter(range => 
      range.gender === 'all' || 
      range.gender === patientGender.toLowerCase() ||
      (range.gender === 'other' && !['male', 'female'].includes(patientGender.toLowerCase()))
    );

    if (genderRanges.length === 0) {
      return {
        range_from: null,
        range_to: null,
        cric_range_from: null,
        cric_range_to: null,
        displayText: parameter.referenceRangeText || 'N/A',
        matched: false,
        matchType: 'no_gender_match'
      };
    }

    // Find exact age match
    const exactMatch = genderRanges.find(range => {
      const fromAge = range.age_from_in_days || (range.age_from ? range.age_from * 365 : 0);
      const toAge = range.age_to_in_days || (range.age_to ? range.age_to * 365 : Number.MAX_SAFE_INTEGER);
      
      return ageInDays >= fromAge && ageInDays <= toAge;
    });

    if (exactMatch) {
      return {
        ...exactMatch,
        displayText: this.formatRangeText(exactMatch),
        matched: true,
        matchType: 'exact',
        ageInDays,
        patientGender
      };
    }

    // Find nearest range if no exact match
    const nearestRange = this.findNearestRange(genderRanges, ageInDays);
    
    if (nearestRange) {
      return {
        ...nearestRange,
        displayText: this.formatRangeText(nearestRange) + ' (Nearest)',
        matched: true,
        matchType: 'nearest',
        ageInDays,
        patientGender
      };
    }

    // Fallback to first available range
    const fallbackRange = genderRanges[0];
    return {
      ...fallbackRange,
      displayText: this.formatRangeText(fallbackRange) + ' (General)',
      matched: true,
      matchType: 'fallback',
      ageInDays,
      patientGender
    };
  }

  // Find nearest reference range by age
  static findNearestRange(ranges, targetAgeInDays) {
    if (ranges.length === 0) return null;

    let nearestRange = ranges[0];
    let minDistance = Number.MAX_SAFE_INTEGER;

    ranges.forEach(range => {
      const fromAge = range.age_from_in_days || (range.age_from ? range.age_from * 365 : 0);
      const toAge = range.age_to_in_days || (range.age_to ? range.age_to * 365 : Number.MAX_SAFE_INTEGER);
      
      // Calculate distance to range midpoint
      const midpoint = (fromAge + toAge) / 2;
      const distance = Math.abs(targetAgeInDays - midpoint);
      
      if (distance < minDistance) {
        minDistance = distance;
        nearestRange = range;
      }
    });

    return nearestRange;
  }

  // Format range for display
  static formatRangeText(range) {
    if (!range.range_from && !range.range_to) {
      return 'N/A';
    }
    
    if (range.range_from !== null && range.range_to !== null) {
      return `${range.range_from} - ${range.range_to}`;
    } else if (range.range_from !== null) {
      return `> ${range.range_from}`;
    } else if (range.range_to !== null) {
      return `< ${range.range_to}`;
    }
    
    return 'N/A';
  }

  // Determine result status based on value and reference range
  static getResultStatus(value, referenceRange) {
    if (value === null || value === undefined || !referenceRange) {
      return 'normal';
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'normal';

    // Check critical ranges first
    if (referenceRange.cric_range_from !== null && numericValue < referenceRange.cric_range_from) {
      return 'critical_low';
    }
    if (referenceRange.cric_range_to !== null && numericValue > referenceRange.cric_range_to) {
      return 'critical_high';
    }

    // Check normal ranges
    if (referenceRange.range_from !== null && numericValue < referenceRange.range_from) {
      return 'low';
    }
    if (referenceRange.range_to !== null && numericValue > referenceRange.range_to) {
      return 'high';
    }

    return 'normal';
  }

  // Get match quality badge
  static getMatchBadge(matchType) {
    const badges = {
      exact: { color: 'success', label: 'Exact Match', tooltip: 'Perfect age and gender match' },
      nearest: { color: 'warning', label: 'Nearest Match', tooltip: 'Closest age range available' },
      fallback: { color: 'info', label: 'General Range', tooltip: 'Using general reference range' },
      text: { color: 'default', label: 'Text Range', tooltip: 'Using text reference range' }
    };
    
    return badges[matchType] || badges.text;
  }
}